/**
 * Fleet mood engine — the single derived state object the Fleet HUD cinema
 * layer binds to (doctrine: docs/plans/2026-08-12-hud-cinema-consult.md).
 * Pure derivation from REAL payload fields only — no synthesized state.
 *
 * Priority order (first match wins):
 *   down     — orchestrator_alive === false
 *   halted   — latest run human_escalated, or any escalation needs a human
 *   healing  — active run whose most recent OPEN session is a fixer/second_opinion
 *   building — active run AND journal_age_seconds < 90  (fleet is talking)
 *   waiting  — active run AND journal_age_seconds >= 90 (silent stage / war room)
 *   idle     — alive, nothing active
 */
import { useEffect, useState } from 'react';
import {
  FleetEscalation,
  FleetOverview,
  FleetSession,
} from '../../services/fleetHudApi';
import { parseTs } from './fleetTime';

export type FleetMood = 'down' | 'halted' | 'healing' | 'building' | 'waiting' | 'idle';

// Moved verbatim from NowCard.tsx (2026-08-12) — mirrors ACTIVE_STATUSES in
// backend/app/api/v1/fleet_hud.py. A run is active ONLY in these states;
// every other status (completed_with_warnings, failed_senior_review,
// interrupted, ...) is terminal. Do not edit one copy without the backend.
export const ACTIVE_STATUSES: Set<string> = new Set([
  'in_progress',
  'running',
  'pending',
  'queued',
  'starting',
]);

// Agent types that mean the fleet is actively repairing something.
const HEALING_AGENT_TYPES: Set<string> = new Set(['fixer', 'second_opinion']);

/** Journal younger than this (seconds) means the fleet is emitting output now. */
export const JOURNAL_HOT_SECONDS = 90;

function isActiveStatus(status?: string): boolean {
  return !!status && ACTIVE_STATUSES.has(status);
}

/** Open = not completed. Most recent = latest started_at (fallback: updated_at). */
function mostRecentOpenSession(sessions: FleetSession[], runId: string): FleetSession | null {
  const open = sessions.filter(
    (s) => s.run_id === runId && (s.completed_at === null || s.completed_at === undefined)
  );
  if (open.length === 0) return null;
  const recency = (s: FleetSession): number =>
    parseTs(s.started_at) ?? parseTs(s.updated_at) ?? 0;
  return open.reduce((a, b) => (recency(a) >= recency(b) ? a : b));
}

export function deriveMood(
  overview: FleetOverview,
  escalations: FleetEscalation[],
  sessions: FleetSession[]
): FleetMood {
  // (1) down — cold metal; nothing else matters.
  if (overview.orchestrator_alive === false) return 'down';

  const run = overview.latest_run;

  // (2) halted — a human is blocking the line.
  if (
    run?.status === 'human_escalated' ||
    escalations.some((e) => e.human_escalation_required === true)
  ) {
    return 'halted';
  }

  if (run && isActiveStatus(run.status)) {
    // (3) healing — the fleet's most recent open session is a repair agent.
    const open = mostRecentOpenSession(sessions, run.run_id);
    if (open?.agent_type && HEALING_AGENT_TYPES.has(open.agent_type)) return 'healing';

    // (4)/(5) building vs waiting — journal age is the truth-to-cinema
    // transducer: < 90s the fleet is talking; >= 90s it's a silent stage.
    const age = overview.journal_age_seconds;
    if (age !== null && age !== undefined && age < JOURNAL_HOT_SECONDS) return 'building';
    return 'waiting';
  }

  // (6) idle — alive, banked coals.
  return 'idle';
}

/**
 * The honest heartbeat — every ticking element (journal age, poll countdown)
 * recomputes `now − realTimestamp` off this. Client-side interpolation of a
 * real, monotonic quantity is a lawful motion source; simulating is not.
 */
export function useNow(intervalMs: number = 1000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Forge metaphor: heat = truth. Glow is the page-grade CSS color bound to
 * --mood-glow; palette is strictly near-black + #FF6600 (no cyan/teal/navy/gold). // design-law-allow
 * pulseSeconds drives mood-bound pulse rates elsewhere (0 = no animation).
 */
export const MOOD_STYLE: Record<FleetMood, { glow: string; pulseSeconds: number; label: string }> = {
  down: { glow: 'rgba(255, 102, 0, 0)', pulseSeconds: 0, label: 'FLEET DOWN' },
  halted: { glow: 'rgba(255, 102, 0, 0.45)', pulseSeconds: 0.8, label: 'HALTED — NEEDS HUMAN' },
  healing: { glow: 'rgba(255, 102, 0, 0.30)', pulseSeconds: 2.0, label: 'HEALING' },
  building: { glow: 'rgba(255, 102, 0, 0.35)', pulseSeconds: 1.6, label: 'BUILDING' },
  waiting: { glow: 'rgba(255, 102, 0, 0.20)', pulseSeconds: 2.4, label: 'WAITING — SILENT STAGE' },
  idle: { glow: 'rgba(255, 102, 0, 0.10)', pulseSeconds: 6.0, label: 'IDLE — BANKED COALS' },
};
